<template>
  <v-container class="mt-4 ml-2" fluid>
    <v-row>
      <v-col cols="12">
        <v-card class="glass-card mb-4" elevation="0">
          <v-card-title class="d-flex align-center pb-2 pt-4 px-6">
            <h2 class="text-h5 font-weight-bold primary--text mb-0">
              Mis Videos Subidos
            </h2>
            <v-spacer></v-spacer>

            <!-- Filtro de escuela (Oculto si se envía vía prop) -->
            <v-select
              v-if="!escuelaProp"
              v-model="escuelaSel"
              :items="escuelas"
              item-text="nombre"
              item-value="id"
              label="Escuela"
              outlined
              dense
              hide-details
              class="mr-4"
              style="max-width: 150px"
              @change="initialize()"
            ></v-select>

            <v-btn
              class="mr-3 premium-btn text-capitalize font-weight-bold"
              color="orange"
              dark
              rounded
              @click="abrirModalSubir()"
            >
              <v-icon left>mdi-cloud-upload</v-icon>
              Subir Video
            </v-btn>

            <v-btn
              class="premium-btn text-capitalize font-weight-bold"
              color="primary"
              dark
              rounded
              @click="initialize()"
            >
              <v-icon left>mdi-refresh</v-icon>
              Actualizar
            </v-btn>
          </v-card-title>

          <v-card-text class="px-6 pb-6 pt-0">
            <v-row justify="end" class="mb-4 mt-1">
              <v-col cols="12" md="3" lg="2">
                <v-select
                  v-model="estadoSel"
                  :items="estados"
                  label="Estado del video"
                  dense
                  outlined
                  rounded
                  hide-details
                  color="primary"
                ></v-select>
              </v-col>
              <v-col cols="12" md="4" lg="3">
                <v-text-field
                  label="Buscar videos..."
                  dense
                  outlined
                  rounded
                  hide-details
                  prepend-inner-icon="mdi-magnify"
                  v-model="search"
                  class="search-input"
                  color="primary"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Loading State -->
            <v-row v-if="cargar" justify="center" class="py-12">
              <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
            </v-row>

            <!-- Empty State -->
            <v-row v-else-if="videosFiltrados.length === 0" justify="center" class="py-12 text-center">
              <v-col cols="12" md="6">
                <v-icon size="80" color="grey lighten-2" class="mb-4">mdi-video-off-outline</v-icon>
                <h3 class="text-h6 grey--text font-weight-medium">No se encontraron videos</h3>
                <p class="grey--text text--darken-1">Sube un nuevo video para comenzar.</p>
              </v-col>
            </v-row>

            <!-- Cards Grid -->
            <v-row v-else>
              <v-col
                v-for="item in videosFiltrados"
                :key="item.id_video_merca"
                cols="12"
                sm="6"
                md="4"
                lg="4"
                xl="3"
              >
                <v-card class="video-card rounded-lg transition-swing" hover elevation="3">
                  <!-- Thumbnail Placeholder -->
                  <div class="thumbnail-wrapper d-flex align-center justify-center bg-grey-lighten-4 pointer" @click="abrirVideo(item)">
                    <div class="play-overlay d-flex flex-column align-center justify-center" v-if="item.ruta_video">
                      <v-icon size="56" color="white" class="play-icon">mdi-play-circle</v-icon>
                      <span class="white--text font-weight-bold mt-1 text-shadow">Ver Video</span>
                    </div>
                    <v-icon v-else size="64" color="grey lighten-1">mdi-play-circle-outline</v-icon>
                    
                    <!-- Status Badge -->
                    <div class="status-badge">
                      <v-chip
                        small
                        class="font-weight-bold px-3 elevation-2"
                        :color="getStatusColor(item.estatus).bg"
                        :text-color="getStatusColor(item.estatus).text"
                      >
                        <v-icon left small>{{ getStatusIcon(item.estatus) }}</v-icon>
                        {{ item.estatus }}
                      </v-chip>
                    </div>
                  </div>

                  <v-card-text class="pa-4">
                    <div class="d-flex align-start mb-2">
                      <v-avatar size="36" color="primary lighten-4" class="mr-3 mt-1">
                        <v-icon color="primary">mdi-account</v-icon>
                      </v-avatar>
                      <div>
                        <div class="text-subtitle-2 font-weight-bold text-truncate" style="max-width: 180px;" v-if="!item.nombre_video">
                          Video #{{ item.id_video_merca }}
                        </div>
                        <div class="text-subtitle-2 font-weight-bold text-truncate" style="max-width: 180px;" v-else>
                          {{ item.nombre_video }}
                        </div>
                        <div class="text-caption grey--text d-flex align-center">
                          <v-icon x-small class="mr-1 grey--text">mdi-account</v-icon>
                          {{ item.nombre }}
                        </div>
                        <div class="text-caption grey--text d-flex align-center">
                          <v-icon x-small class="mr-1 grey--text">mdi-calendar</v-icon>
                          {{ item.fecha_subida }}
                        </div>
                      </div>
                    </div>

                    <v-divider class="my-3"></v-divider>

                    <!-- Rechazo Info -->
                    <div v-if="item.estatus === 'RECHAZADO'" class="rechazo-box rounded pa-2 d-flex align-start mt-2">
                      <v-icon small color="error" class="mr-2 mt-1">mdi-alert-circle</v-icon>
                      <div>
                        <div class="text-caption font-weight-bold error--text">Motivo de rechazo:</div>
                        <div class="text-caption grey--text text--darken-2" style="line-height: 1.2;">
                          {{ item.motivo_rechazo || 'No especificado' }}
                        </div>
                      </div>
                    </div>
                    
                    <!-- Approved Info -->
                    <div v-else-if="item.estatus === 'ACEPTADO'" class="approved-box rounded pa-2 d-flex align-center justify-center mt-2">
                      <v-icon small color="success" class="mr-2">mdi-check-decagram</v-icon>
                      <span class="text-caption success--text font-weight-bold mb-0">Transferido a Multimedia</span>
                    </div>

                    <!-- Pending Info -->
                    <div v-else class="pending-box rounded pa-2 d-flex align-center justify-center mt-2">
                      <v-icon small color="warning" class="mr-2">mdi-clock-outline</v-icon>
                      <span class="text-caption warning--text font-weight-medium mb-0">Esperando revisión...</span>
                    </div>

                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialog para subir nuevo video -->
    <v-dialog v-model="dialogSubir" max-width="550px" persistent>
      <v-card class="rounded-lg overflow-hidden">
        <v-card-title class="py-4 px-6 d-flex align-center">
          <v-icon class="mr-3">mdi-cloud-upload-outline</v-icon>
          <span class="text-h6 font-weight-bold">Subir Nuevo Video</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="dialogSubir = false" :disabled="btnLoading">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text class="pt-6 px-6">
          <div class="text-body-1 mb-4 grey--text text--darken-2">
            Selecciona el video que deseas enviar a validación por el departamento de mercadotecnia.
          </div>
          
          <v-file-input
            v-model="archivoVideo"
            label="Seleccionar archivo de video"
            accept="video/mp4,video/quicktime,video/x-msvideo"
            outlined
            color="primary"
            prepend-icon=""
            prepend-inner-icon="mdi-movie-open-play"
            show-size
            class="rounded-lg custom-file-input"
            height="60"
          >
            <template v-slot:selection="{ file }">
              <v-chip color="primary" dark label small class="font-weight-bold">
                {{ file.name }}
              </v-chip>
            </template>
          </v-file-input>
          
          <div class="d-flex align-center mt-2">
            <v-icon small color="info" class="mr-2">mdi-information</v-icon>
            <span class="text-caption grey--text">
              Formatos recomendados: MP4, MOV, AVI, WMV, FLV, WEBM. Asegúrate de que tenga buena calidad y que no supere los 100MB.
            </span>
          </div>

          <v-divider class="my-4"></v-divider>
          <v-select
              v-model="escuelaSel"
              :items="escuelas"
              v-if="[12,97,25,48,74,11,43,74,97,101,25].includes(getdatosUsuarioERPERP.idpuesto)"
              item-text="nombre"
              item-value="id"
              label="Escuela"
              outlined
              dense
              hide-details
              class="my-4"
              prepend-icon="mdi-school-outline"
              @change="initialize()"
            ></v-select>
          <div class="d-flex align-center">
            <v-text-field
              v-model="nombreVideo"
              label="Nombre del video"
              outlined
              color="primary"
              prepend-icon="mdi-comment-text-outline"
              class="rounded-lg custom-text-field"
              height="60"
            ></v-text-field>
          </div>
        </v-card-text>
        
        <v-card-actions class="px-6 pb-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn 
            color="grey darken-1" 
            text 
            rounded 
            class="px-6 font-weight-bold"
            @click="dialogSubir = false" 
            :disabled="btnLoading"
          >
            Cancelar
          </v-btn>
          <v-btn 
            color="primary" 
            rounded 
            class="px-8 shadow-blue font-weight-bold text-capitalize"
            @click="guardarVideo" 
            :loading="btnLoading"
          >
            <v-icon left>mdi-send-check</v-icon>
            Enviar a Revisión
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog para visualizar el video -->
    <v-dialog v-model="dialogVideo" max-width="800px" overlay-color="#000" overlay-opacity="0.8">
      <v-card class="rounded-lg overflow-hidden" style="background-color: #0F0F0F !important; border: 1px solid #333 !important;">
        <v-card-title class="d-flex align-center py-3 px-4" style="background: #ffffff; border-bottom: 1px solid #222;">
          <v-avatar size="32" color="primary" class="mr-3">
            <v-icon dark small>mdi-video</v-icon>
          </v-avatar>
          <span class="text-subtitle-1 font-weight-bold">Video #{{ videoSelect ? videoSelect.id_video_merca : '' }}</span>
          <v-spacer></v-spacer>
          <v-btn icon color="primary" class="mr-2" @click="descargarVideo()">
            <v-icon>mdi-download</v-icon>
          </v-btn>
          <v-btn icon dark @click="dialogVideo = false">
            <v-icon color="black">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <div style="background-color: #0F0F0F; display: flex; justify-content: center; align-items: center; min-height: 300px;">
          <video id="video-recepcion" :src="urlVideoActual" frameborder="0" controls controlsList="nodownload"
                 allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                 style="width: 100%; max-height: 80vh; outline: none; box-shadow: 0 4px 30px rgba(0,0,0,0.5);">
          </video>
        </div>
      </v-card>
    </v-dialog>

    <Alerta v-if="respuestaAlerta" :parametros="parametros" />
    <carga v-if="btnLoading && !dialogSubir" />
  </v-container>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import Alerta from '@/components/alertas/Alerta.vue';
import carga from '@/components/alertas/carga.vue';
import validarErrores from '@/mixins/validarErrores';
import moment from "moment";
moment.locale('es');

export default {
  mixins: [validarErrores],
  components: {
    Alerta,
    carga
  },
  props: {
    escuelaProp: {
      type: Number,
      default: null
    }
  },
  data: () => ({
    parametros: {
      dialogError: false,
      mensaje: '',
      color: ''
    },
    respuestaAlerta: false,
    cargar: false,
    dialogSubir: false,
    dialogVideo: false,
    videoSelect: null,
    urlVideoActual: '',
    btnLoading: false,
    search: '',
    archivoVideo: null,
    videos: [],
    escuelaSel: 1,
    escuelas: [
      { id: 1, nombre: 'INBI' },
      { id: 2, nombre: 'FAST' }
    ],
    estadoSel: 'TODOS',
    estados: ['TODOS', 'ACEPTADO', 'RECHAZADO', 'PENDIENTE'],
    nombreVideo: ''
  }),
  computed: {
    ...mapGetters("login", ["getdatosUsuarioERPERP", "getEscuela"]),
    videosFiltrados() {
      let filtered = this.videos;

      console.log( this.videos )
      
      // Filtro de "mis videos" de forma local si está habilitado mediante lógica, por seguridad (asegurarse de ver solo mis subidas)
      if (![12, 97, 25, 48, 74, 11, 43, 74, 97, 101, 25 ].includes(this.getdatosUsuarioERPERP.idpuesto) && (this.getdatosUsuarioERPERP && this.getdatosUsuarioERPERP.iderp)) {
        filtered = filtered.filter(v => v.id_usuario_recepcion == this.getdatosUsuarioERPERP.iderp);
      }
      
      // Filtro por Estado (el estatus en BD es ACEPTADO, RECHAZADO, PENDIENTE)
      if (this.estadoSel !== 'TODOS') {
        const estadoCmp = this.estadoSel === 'PENDIENTE' ? 'PENDIENTE' : this.estadoSel; //Ajustar PENDIENTES plural
        filtered = filtered.filter(v => v.estatus && v.estatus.toUpperCase() === estadoCmp);
      }

      // Filtro de Búsqueda
      if (this.search) {
        const term = this.search.toLowerCase();
        filtered = filtered.filter(v => {
          return (
            (v.estatus && v.estatus.toLowerCase().includes(term)) ||
            (v.id_video_merca && v.id_video_merca.toString().includes(term)) ||
            (v.motivo_rechazo && v.motivo_rechazo.toLowerCase().includes(term))
          );
        });
      }
      return filtered;
    }
  },
  watch: {
    dialogVideo(val) {
        if (!val) {
            const videoElement = document.getElementById('video-recepcion');
            if (videoElement) {
                videoElement.pause();
                videoElement.currentTime = 0;
            }
        }
    },
    escuelaProp(newVal) {
      if (newVal) {
        this.escuelaSel = newVal;
        this.initialize();
      }
    }
  },
  created() {
    if (this.escuelaProp) {
      this.escuelaSel = this.escuelaProp;
    }
    this.initialize();
  },
  methods: {
    formatearFecha(fecha) {
        if (!fecha) return "";
        return moment(fecha).format("DD MMM YYYY, HH:mm");
    },
    getStatusColor(status) {
      if (status === 'ACEPTADO') return { bg: 'success lighten-5', text: 'success darken-2' };
      if (status === 'RECHAZADO') return { bg: 'error lighten-5', text: 'error darken-2' };
      return { bg: 'warning lighten-5', text: 'warning darken-4' }; // PENDIENTE
    },
    getStatusIcon(status) {
      if (status === 'ACEPTADO') return 'mdi-check-circle';
      if (status === 'RECHAZADO') return 'mdi-close-circle';
      return 'mdi-clock-fast';
    },
    initialize() {
      if (!this.getdatosUsuarioERPERP || !this.getdatosUsuarioERPERP.iderp) return;
      
      this.cargar = true;
      this.videos = [];
      
      const payload = { params: { escuela: this.escuelaSel, id_puesto: this.getdatosUsuarioERPERP.idpuesto } };

      this.$http.get(`videos.merca.recepcion/${this.getdatosUsuarioERPERP.iderp}`, payload)
        .then(response => {
           this.videos = response.data.map( v => {
               v.fecha_subida = this.formatearFecha(v.fecha_subida);
               return v;
           });
        })
        .catch(error => {
           const msg = (error.response && error.response.data) ? error.response.data.message : 'Error al obtener videos';
          this.validarError(msg);
        })
        .finally(() => {
           this.cargar = false;
        });
    },
    abrirModalSubir() {
      this.archivoVideo = null;
      this.dialogSubir = true;
    },
    abrirVideo(item) {
       if (!item.ruta_video) {
           this.validarError("Este registro no tiene un video asociado válido.");
           return;
       }
       this.videoSelect = item;
       const baseUrl = this.$http.defaults.baseURL || '';
       const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
       this.urlVideoActual = base + item.ruta_video;
       this.dialogVideo = true;
    },
    async descargarVideo() {
        if (!this.urlVideoActual) return;
        try {
            const response = await fetch(this.urlVideoActual);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = `Video_${this.videoSelect.id_video_merca}.mp4`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            window.open(this.urlVideoActual, '_blank');
        }
    },
    async guardarVideo() {
        if (!this.archivoVideo) {
            this.validarError("Por favor selecciona un archivo de video");
            return;
        }
        if (!this.nombreVideo) {
            this.validarError("Por favor ingresa un nombre para el video");
            return;
        }

        this.btnLoading = true;

        try {
            let formData = new FormData();
            formData.append("file", this.archivoVideo);
            formData.append("id_usuario_recepcion", this.getdatosUsuarioERPERP.iderp);
            formData.append("escuela", this.escuelaSel);
            formData.append("nombre", this.nombreVideo);

            await this.$http.post('videos.merca.upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            this.validarSuccess("Video enviado a validación correctamente");
            this.dialogSubir = false;
            this.initialize();

        } catch (error) {
            console.error(error);
            const msg = (error.response && error.response.data) ? error.response.data.message : "Ocurrió un error al subir el video";
            this.validarError(msg);
        } finally {
            this.btnLoading = false;
        }
    }
  }
};
</script>

<style scoped>
/* Glassmorphism & Cards */
.glass-card {
  background: white !important;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.04) !important;
  border-radius: 16px !important;
}

.glass-dialog {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0 16px 40px 0 rgba(0, 0, 0, 0.15) !important;
}

/* Video Cards */
.video-card {
  border: 1px solid rgba(0,0,0,0.05) !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.video-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 24px -4px rgba(0,0,0,0.12) !important;
  border-color: rgba(0,0,0,0.1) !important;
}

.thumbnail-wrapper {
  height: 180px;
  position: relative;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%);
  border-bottom: 1px solid rgba(0,0,0,0.05);
}

.status-badge {
  position: absolute;
  top: 12px;
  right: 12px;
}

/* Info Boxes */
.rechazo-box {
  background-color: #fff5f5;
  border: 1px dashed #ffcdd2;
}

.approved-box {
  background-color: #f1fbf5;
  border: 1px dashed #c8e6c9;
}

.pending-box {
  background-color: #fff9e6;
  border: 1px dashed #ffecb3;
}

/* Buttons */
.premium-btn {
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
}

.premium-btn:hover {
  transform: translateY(-2px);
}

/* Inputs */
.search-input .v-input__control > .v-input__slot {
  background: white !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04) !important;
}

.custom-file-input .v-input__control > .v-input__slot {
  border-radius: 12px !important;
}

.play-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.3);
  opacity: 0.8;
  transition: all 0.3s ease;
  z-index: 2;
}

.thumbnail-wrapper:hover .play-overlay {
  background: rgba(0,0,0,0.5);
  opacity: 1;
}

.play-icon {
  transition: transform 0.3s ease;
}

.thumbnail-wrapper:hover .play-icon {
  transform: scale(1.15);
}

.pointer {
  cursor: pointer;
}
</style>
